'use client'

import { Dialog as BaseDialog } from '@base-ui/react/dialog'
import { useState } from 'react'

import { cn } from '@/core/utils/common'

import { LayerHeadSlot } from '@/features/veluvit/components/overlay/layer-slot'
import PaymentPopupHost from '@/features/veluvit/components/wallet/payment-popup-host'

import {
  useOverlayStore,
  type Layer,
} from '@/features/veluvit/stores/overlay.store'

import type { ReactNode } from 'react'

/* 레이어 모양 — 모달은 가운데 카드, 시트는 모바일에서 아래서 올라오는 서랍·PC 에선 가운데 카드.
   Tailwind v4 의 scale·translate 는 개별 속성이라 transition 목록에 이름을 직접 적는다. */
// 열 때는 부드러운 스프링 곡선으로 조금 길게(아래에서 살짝 떠오르며 커진다), 닫을 때는 짧고 가볍게
const MOTION =
  'transition-[opacity,scale,translate] duration-[380ms] ease-smooth data-[ending-style]:duration-200 data-[ending-style]:ease-in'
// 모든 모달 · 시트(충전소 · 해금 · 페이월 …)가 퀘스트창과 같은 글래스 틀을 쓴다 — 옛 보라 면(#1c1a27) 대신
// 판은 [머리 칸 | 본문 스크롤 칸] 세로 두 칸 — 판 자체는 스크롤하지 않는다(PanelBody)
const PANEL = 'relative flex flex-col overflow-hidden'
// 레이어는 처음부터 열린 채 붙는다 — Base UI 의 data-[starting-style] 은 닫힘→열림 전환에만 붙어 등장 연출이 없었다.
// 그래서 등장은 CSS @starting-style(starting:)로, 퇴장은 data-[ending-style] 로 건다
const POP =
  'starting:translate-y-3 starting:scale-[0.92] starting:opacity-0 data-[starting-style]:translate-y-3 data-[starting-style]:scale-[0.92] data-[starting-style]:opacity-0 data-[ending-style]:scale-[0.96] data-[ending-style]:opacity-0'

/**
 * 판 안쪽 — 위는 머리 칸(SheetHead 가 포털로 들어온다, 스크롤 밖), 아래는 본문만 스크롤하는 칸.
 * 머리가 판과 같은 유리 위에 그대로 있고, 본문은 머리 아래 선에서 잘린다 — sticky 머리에 진한 띠를 깔아 가리던 방식을 대신한다.
 * 머리가 없는 모달(CloseX 를 본문에 직접 두는 것)은 머리 칸이 비어 접히고(empty:hidden) 본문이 판 위 여백부터 시작한다.
 */
function PanelBody({ children }: { children: ReactNode }) {
  const [slot, setSlot] = useState<HTMLElement | null>(null)
  return (
    <>
      <div
        ref={setSlot}
        className="peer flex-none px-(--layer-px) pt-(--layer-pt) pb-1 empty:hidden"
      />
      {/* 본문 위 10px 는 투명 → 불투명으로 번지게 — 머리 아래로 사라지는 글이 칼같이 잘리지 않고 유리 속으로 녹는다(쉬는 자리에선 위 여백이라 안 보인다) */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain scrollbar-none px-(--layer-px) pt-3 pb-(--layer-pb) [mask-image:linear-gradient(to_bottom,transparent,#000_10px)] peer-empty:pt-(--layer-pt)">
        <LayerHeadSlot.Provider value={slot}>{children}</LayerHeadSlot.Provider>
      </div>
    </>
  )
}

function LayerView({
  layer,
  top,
  suspended,
}: {
  layer: Layer
  top: boolean
  suspended: boolean
}) {
  const close = useOverlayStore((s) => s.close)
  const sheet = layer.kind === 'sheet'
  // 왼쪽 서랍(햄버거 메뉴) — 시트와 같은 판 · 머리 칸 구조, 나오는 방향과 모양만 다르다
  const drawer = sheet && layer.side === 'left'
  const panel = cn(
    PANEL,
    layer.surface === 'solid'
      ? 'bg-background shadow-[var(--shadow-floating)]'
      : 'glass-overlay'
  )

  return (
    <BaseDialog.Root
      open={!layer.closing}
      // 맨 위 레이어만 모달(포커스 가둠·바깥 차단)로 두고, 아래 레이어는 가만히 둔다 — core ModalContainer 와 같은 규칙
      // SDK가 body에 여는 결제 레이어로 포커스를 넘기고, 기존 시트의 상태는 유지한다.
      modal={top && !suspended}
      disablePointerDismissal={suspended || !top || layer.lock}
      onOpenChange={(open) => {
        // 약관·인증처럼 반드시 거쳐야 하는 단계(lock)는 Esc·바깥 누르기로 닫지 않는다
        if (!open && top && !suspended && !layer.lock) close(layer.id)
      }}
    >
      <BaseDialog.Portal>
        <BaseDialog.Backdrop
          className={cn(
            'fixed inset-0 z-[200] transition-opacity duration-300 ease-out starting:opacity-0 data-[starting-style]:opacity-0 data-[ending-style]:opacity-0 data-[ending-style]:duration-200',
            // 바깥 막은 어둡게만 — 흐림은 모달 판(glass-overlay) 한 겹에만
            // 막이 너무 어두우면 판 뒤로 비칠 게 없어 글래스가 죽는다 — 0.6 → 0.45
            layer.clear ? 'bg-black/30' : 'bg-black/45',
            !top && 'opacity-0',
            suspended && 'invisible pointer-events-none'
          )}
        />
        <BaseDialog.Viewport
          inert={suspended}
          className={cn(
            'fixed inset-0 z-[200] flex',
            drawer
              ? 'items-stretch justify-start'
              : sheet
                ? 'items-end justify-center over10:items-center over10:p-6'
                : 'items-center justify-center p-4',
            suspended && 'invisible pointer-events-none'
          )}
        >
          <BaseDialog.Popup
            data-layer-id={layer.id}
            aria-label={layer.label}
            // role 은 raw 일 때만 덮는다 — undefined 를 넘기면 Base UI 기본값(dialog)까지 지워진다
            {...(layer.kind === 'raw' ? { role: 'status' } : {})}
            className={cn(
              // 시트는 폭 전환까지 포함한 자기 transition 을 쓴다(아래) — 둘을 같이 붙이면 어느 쪽이 이길지 정해지지 않는다
              !sheet && MOTION,
              layer.kind === 'modal' &&
                !layer.bare &&
                cn(
                  panel,
                  POP,
                  'max-h-[calc(100dvh-64px)] w-full max-w-[420px] rounded-overlay [--layer-pb:1.25rem] [--layer-px:1.5rem] [--layer-pt:1.5rem]'
                ),
              layer.kind === 'modal' && layer.bare && POP,
              drawer &&
                cn(
                  panel,
                  'transition-[opacity,translate] duration-[360ms] ease-smooth data-[ending-style]:duration-200 data-[ending-style]:ease-in',
                  // 모바일 기둥(최대 480, 가운데) 왼쪽 끝에 맞춘다 — 태블릿에서 창 끝에 떨어져 붙지 않게
                  'ml-[max(0px,calc((100vw-480px)/2))] h-full w-[min(300px,84%)] rounded-r-overlay pt-(--safe-top) [--layer-pb:max(24px,env(safe-area-inset-bottom))] [--layer-pt:1rem] [--layer-px:1.25rem]',
                  'starting:-translate-x-full data-[starting-style]:-translate-x-full data-[ending-style]:-translate-x-full'
                ),
              sheet &&
                !drawer &&
                cn(
                  panel,
                  // 시트 안 이동(sheet-nav)이 --sheet-w 로 PC 폭을 넓히면 부드럽게 따라간다
                  'transition-[opacity,scale,translate,max-width] duration-[460ms] ease-smooth data-[ending-style]:duration-[240ms] data-[ending-style]:ease-in',
                  'max-h-[88dvh] w-full rounded-t-overlay pt-2.5 [--layer-pb:max(24px,env(safe-area-inset-bottom))] [--layer-px:1.25rem] [--layer-pt:0.625rem] over10:pt-0 over10:[--layer-pb:1.5rem] over10:[--layer-px:1.5rem] over10:[--layer-pt:1.5rem]',
                  'starting:translate-y-full data-[starting-style]:translate-y-full data-[ending-style]:translate-y-full',
                  'over10:max-h-[86vh] over10:max-w-[var(--sheet-w,540px)] over10:rounded-overlay',
                  'over10:starting:translate-y-4 over10:starting:scale-[0.96] over10:starting:opacity-0 over10:data-[starting-style]:translate-y-4 over10:data-[starting-style]:scale-[0.96] over10:data-[starting-style]:opacity-0 over10:data-[ending-style]:translate-y-2 over10:data-[ending-style]:opacity-0',
                  layer.tall && 'h-[88dvh] over10:h-[86vh]'
                ),
              layer.kind === 'raw' &&
                'starting:opacity-0 data-[starting-style]:opacity-0 data-[ending-style]:opacity-0',
              layer.className
            )}
          >
            {sheet && !drawer && (
              <div
                aria-hidden="true"
                className="mx-auto mb-4 h-1 w-10 flex-none rounded-full bg-foreground/20 over10:hidden"
              />
            )}
            {sheet || (layer.kind === 'modal' && !layer.bare) ? (
              <PanelBody>{layer.content}</PanelBody>
            ) : (
              layer.content
            )}
          </BaseDialog.Popup>
        </BaseDialog.Viewport>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  )
}

/**
 * veluvit 레이어(모달·시트) — 여러 장이 겹쳐 뜨고(해금 → 충전) 맨 위 것만 Esc·바깥 누르기에 반응한다.
 * 포커스 가두기·스크롤 잠금·포털·열고 닫는 애니메이션은 Base UI Dialog(core 모달과 같은 기반)가 맡는다.
 * 여는 쪽 API(openModal/openSheet/closeTop …)는 그대로다.
 */
export default function LayerHost() {
  const layers = useOverlayStore((s) => s.layers)
  const suspended = useOverlayStore((s) => s.externalLayerId !== null)
  const live = layers.filter((l) => !l.closing)
  const topId = live[live.length - 1]?.id
  return (
    <>
      <PaymentPopupHost />
      {layers.map((layer) => (
        <LayerView
          key={layer.id}
          layer={layer}
          top={layer.id === topId}
          suspended={suspended}
        />
      ))}
    </>
  )
}
